import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Grid,
  Divider,
  InputAdornment,
  Paper,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PremiumFeatureGate from "../components/PremiumFeatureGate";
import { MaintenanceAPI } from "../api/maintenance";

const STATUS_STYLES = {
  requested: { label: "Requested", color: "info" },
  active: { label: "Scheduled", color: "success" },
  paused: { label: "Paused", color: "default" },
  completed: { label: "Completed", color: "success" },
  cancelled: { label: "Cancelled", color: "default" },
};

const INFO_TEXT = {
  requested: "Incoming work requests awaiting triage or scheduling.",
  scheduled: "Open maintenance jobs with a planned due date.",
  completed: "Closed work orders retained for audit review.",
  reports: "Evidence files and service documents attached to a record.",
  invoices: "Service invoices attached to maintenance work orders.",
};

const DEFAULT_FORM = {
  target_type: "ASSET",
  target_name: "",
  target_scope: "",
  cadence: "MONTHLY",
  reminder_days: 7,
  vendor_contact_number: "",
  notes: "",
  next_due_at: "",
};

function StatCard({ label, value, hint, color = "#0f172a" }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #dbe5f1", minHeight: 112, background: "linear-gradient(180deg, #fff 0%, #f8fbff 100%)" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900, color, lineHeight: 1, mt: 0.5 }}>
            {value}
          </Typography>
        </Box>
        {hint ? (
          <Tooltip title={hint} arrow placement="top-start">
            <Box sx={{ display: "inline-flex", mt: -0.25 }}>
              <InfoOutlinedIcon fontSize="inherit" />
            </Box>
          </Tooltip>
        ) : null}
      </Stack>
    </Paper>
  );
}

function SectionTitle({ icon, title, subtitle, action }) {
  return (
    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={1.5} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        {icon}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          {subtitle ? <Typography variant="body2" color="text.secondary">{subtitle}</Typography> : null}
        </Box>
      </Stack>
      {action}
    </Stack>
  );
}

function toDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = toDate(value);
  return date ? date.toLocaleDateString() : "-";
}

function formatDateTime(value) {
  const date = toDate(value);
  return date ? date.toLocaleString() : "-";
}

function historyCategory(row) {
  const type = String(row?.schedule?.target_type || row?.details?.target_type || "").trim().toUpperCase();
  return type === "ASSET" ? "asset" : "other";
}

function historyWorkItem(row) {
  return (
    row?.schedule?.target_name ||
    row?.details?.asset_uid ||
    row?.details?.target_name ||
    row?.details?.title ||
    `Maintenance #${String(row?.maintenance_schedule_id || row?.id || "").slice(0, 8)}`
  );
}

function historyScope(row) {
  return row?.schedule?.target_scope || row?.details?.target_scope || row?.details?.source || "-";
}

function historyCategoryLabel(row) {
  return historyCategory(row) === "asset" ? "Asset" : "Other";
}

function normalizeRow(schedule) {
  const statusKey = String(schedule?.status || "active").toLowerCase();
  const status = STATUS_STYLES[statusKey] || STATUS_STYLES.active;
  const metadata = schedule?.metadata && typeof schedule.metadata === "object" ? schedule.metadata : {};
  const latestReport = metadata?.last_completion_report?.summary || metadata?.last_completion_report?.title || "";
  return {
    id: schedule?.id,
    target_type: String(schedule?.target_type || "ASSET").replace(/_/g, " "),
    target_name: schedule?.target_name || "Scheduled item",
    target_scope: schedule?.target_scope || "",
    cadence: schedule?.cadence || "MONTHLY",
    next_due_at: schedule?.next_due_at || null,
    last_completed_at: schedule?.last_completed_at || null,
    vendor_contact_number: schedule?.vendor_contact_number || "",
    notes: schedule?.notes || "",
    latest_report: latestReport,
    report_count: Array.isArray(schedule?.report_items) ? schedule.report_items.length : 0,
    invoice_count: Array.isArray(schedule?.invoice_items) ? schedule.invoice_items.length : 0,
    closure_comment: schedule?.closure_comment || "",
    status_key: statusKey,
    status_label: status.label,
    status_color: status.color,
  };
}

function statusBucket(value) {
  const safe = String(value || "open").toLowerCase();
  if (safe === "open") return ["requested", "active", "paused"];
  if (safe === "all") return ["requested", "active", "paused", "completed", "cancelled"];
  return [safe];
}

function detailLabel(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function detailValue(value, key = "") {
  if (value == null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (/(^|_)(at|date)$/.test(key) || /_at$/.test(key)) return formatDateTime(value);
  return String(value);
}

function friendlyDetails(value, prefix = "") {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) return value.flatMap((item, index) => friendlyDetails(item, `${prefix || "item"} ${index + 1}`));
  if (typeof value !== "object") return [{ label: detailLabel(prefix || "Details"), value: detailValue(value, prefix) }];
  return Object.entries(value).flatMap(([key, item]) => {
    const label = prefix ? `${detailLabel(prefix)} — ${detailLabel(key)}` : detailLabel(key);
    if (item && typeof item === "object") return friendlyDetails(item, label);
    return [{ label, value: detailValue(item, key) }];
  });
}

function isWithinDateRange(value, from, to) {
  const date = toDate(value);
  if (!date) return !from && !to;
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const fromTime = from ? new Date(`${from}T00:00:00`).getTime() : null;
  const toTime = to ? new Date(`${to}T00:00:00`).getTime() : null;
  return (fromTime == null || day >= fromTime) && (toTime == null || day <= toTime);
}

function compareRows(left, right, field, direction) {
  const leftValue = left?.[field];
  const rightValue = right?.[field];
  const leftDate = /(_at|date)$/.test(field) ? toDate(leftValue)?.getTime() || 0 : null;
  const rightDate = /(_at|date)$/.test(field) ? toDate(rightValue)?.getTime() || 0 : null;
  const result = leftDate != null || rightDate != null
    ? leftDate - rightDate
    : typeof leftValue === "number" || typeof rightValue === "number"
      ? Number(leftValue || 0) - Number(rightValue || 0)
      : String(leftValue || "").localeCompare(String(rightValue || ""), undefined, { numeric: true, sensitivity: "base" });
  return direction === "asc" ? result : -result;
}

function MaintenanceRowTable({ rows, onEdit, onUpload, onInvoice, onComplete, onReopen, sort, onSort }) {
  if (!rows.length) {
    return <Alert severity="info">No records match the current filters.</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small" sx={{ minWidth: 1100 }}>
        <TableHead>
          <TableRow>
            {[["target_name", "Work Item"], ["target_type", "Category"], ["target_scope", "Scope"], ["cadence", "Cadence"], ["next_due_at", "Due Date"], ["status_label", "State"], ["vendor_contact_number", "Vendor"], ["notes", "Work details"]].map(([field, label]) => (
              <TableCell key={field} sortDirection={sort.by === field ? sort.dir : false}>
                <TableSortLabel active={sort.by === field} direction={sort.by === field ? sort.dir : "asc"} onClick={() => onSort(field)}>{label}</TableSortLabel>
              </TableCell>
            ))}
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>
                <Stack spacing={0.25}>
                  <Typography sx={{ fontWeight: 700 }}>{row.target_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Closed: {formatDateTime(row.last_completed_at)}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>{row.target_type}</TableCell>
              <TableCell>{row.target_scope || "-"}</TableCell>
              <TableCell>{row.cadence}</TableCell>
              <TableCell>{formatDate(row.next_due_at)}</TableCell>
              <TableCell>
                <Chip size="small" label={row.status_label} color={row.status_color} />
              </TableCell>
              <TableCell>{row.vendor_contact_number || "-"}</TableCell>
              <TableCell>
                <Stack spacing={0.25}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.notes || "No notes added"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.latest_report || (row.report_count ? `${row.report_count} evidence file(s)` : "No service evidence yet")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.invoice_count ? `${row.invoice_count} service invoice(s)` : "No service invoice"}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                  <Button size="small" variant="outlined" startIcon={<CalendarMonthIcon />} onClick={() => onEdit(row)}>
                    {row.status_key === "requested" ? "Plan work" : "Edit plan"}
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<CloudUploadIcon />} onClick={() => onUpload(row)}>
                    Evidence
                  </Button>
                  <Tooltip title="Attach the vendor's service invoice, amount, and invoice number" arrow>
                    <Button size="small" variant="outlined" startIcon={<ReceiptLongIcon />} onClick={() => onInvoice(row)}>
                      Invoice
                    </Button>
                  </Tooltip>
                  {row.status_key !== "completed" ? (
                    <Button size="small" variant="contained" startIcon={<TaskAltIcon />} onClick={() => onComplete(row)}>
                      Close Work Order
                    </Button>
                  ) : (
                    <Button size="small" variant="outlined" startIcon={<RestartAltIcon />} onClick={() => onReopen(row)}>
                      Reopen
                    </Button>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function MaintenancePage() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [dashboard, setDashboard] = React.useState({ stats: {}, schedules: [], history: [] });
  const [createOpen, setCreateOpen] = React.useState(false);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [invoiceOpen, setInvoiceOpen] = React.useState(false);
  const [closeOpen, setCloseOpen] = React.useState(false);
  const [reopenOpen, setReopenOpen] = React.useState(false);
  const [selectedSchedule, setSelectedSchedule] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [reportFiles, setReportFiles] = React.useState([]);
  const [invoiceFiles, setInvoiceFiles] = React.useState([]);
  const [invoiceForm, setInvoiceForm] = React.useState({ invoice_number: "", invoice_amount: "", invoice_date: "" });
  const [closureComment, setClosureComment] = React.useState("");
  const [reopenReason, setReopenReason] = React.useState("");
  const [createForm, setCreateForm] = React.useState(DEFAULT_FORM);
  const [statusFilter, setStatusFilter] = React.useState("open");
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [queueDateFrom, setQueueDateFrom] = React.useState("");
  const [queueDateTo, setQueueDateTo] = React.useState("");
  const [queueSort, setQueueSort] = React.useState({ by: "next_due_at", dir: "asc" });
  const [historyFilter, setHistoryFilter] = React.useState("all");
  const [historyDateFrom, setHistoryDateFrom] = React.useState("");
  const [historyDateTo, setHistoryDateTo] = React.useState("");
  const [historySort, setHistorySort] = React.useState({ by: "performed_at", dir: "desc" });
  const [selectedHistoryRow, setSelectedHistoryRow] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const dashRes = await MaintenanceAPI.dashboard();
      setDashboard(dashRes?.data?.data || { stats: {}, schedules: [], history: [] });
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load maintenance workspace");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const rows = React.useMemo(() => {
    const list = Array.isArray(dashboard?.schedules) ? dashboard.schedules.map(normalizeRow) : [];
    const priority = { requested: 0, active: 1, paused: 2, completed: 3, cancelled: 4 };
    return list.sort((a, b) => (priority[a.status_key] ?? 99) - (priority[b.status_key] ?? 99) || String(a.target_name).localeCompare(String(b.target_name)));
  }, [dashboard]);

  const historyRows = React.useMemo(() => (Array.isArray(dashboard?.history) ? dashboard.history : []), [dashboard]);
  const stats = dashboard?.stats || {};
  const invoiceCount = rows.reduce((count, row) => count + row.invoice_count, 0);
  const typeOptions = React.useMemo(() => {
    const values = new Set(rows.map((row) => String(row.target_type || "").trim()).filter(Boolean));
    return ["all", ...Array.from(values).sort((a, b) => a.localeCompare(b))];
  }, [rows]);

  const visibleRows = React.useMemo(() => {
    const statusSet = new Set(statusBucket(statusFilter));
    const query = String(searchQuery || "").trim().toLowerCase();
    const type = String(typeFilter || "all").toLowerCase();
    return rows.filter((row) => {
      const matchesStatus = statusSet.has(String(row.status_key || "").toLowerCase());
      const matchesType = type === "all" || String(row.target_type || "").toLowerCase() === type;
      const haystack = [row.target_name, row.target_scope, row.notes, row.latest_report, row.vendor_contact_number, row.target_type]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesQuery = !query || haystack.includes(query);
      return matchesStatus && matchesType && matchesQuery && isWithinDateRange(row.next_due_at, queueDateFrom, queueDateTo);
    });
  }, [queueDateFrom, queueDateTo, rows, searchQuery, statusFilter, typeFilter]);

  const sortedVisibleRows = React.useMemo(
    () => [...visibleRows].sort((left, right) => compareRows(left, right, queueSort.by, queueSort.dir)),
    [queueSort, visibleRows]
  );

  const visibleHistoryRows = React.useMemo(() => {
    const filter = String(historyFilter || "all").toLowerCase();
    return historyRows.filter((row) => {
      const category = historyCategory(row);
      return (filter === "all" || category === filter) && isWithinDateRange(row.performed_at, historyDateFrom, historyDateTo);
    });
  }, [historyDateFrom, historyDateTo, historyFilter, historyRows]);

  const sortedHistoryRows = React.useMemo(() => [...visibleHistoryRows].sort((left, right) => {
    const sortableLeft = {
      ...left,
      work_item: historyWorkItem(left),
      category: historyCategoryLabel(left),
      actor_name: left.actor?.email || "system",
    };
    const sortableRight = {
      ...right,
      work_item: historyWorkItem(right),
      category: historyCategoryLabel(right),
      actor_name: right.actor?.email || "system",
    };
    return compareRows(sortableLeft, sortableRight, historySort.by, historySort.dir);
  }), [historySort, visibleHistoryRows]);

  const toggleSort = (setter, field) => setter((current) => ({
    by: field,
    dir: current.by === field && current.dir === "asc" ? "desc" : "asc",
  }));

  const openCreate = () => {
    setSelectedSchedule(null);
    setCreateForm(DEFAULT_FORM);
    setCreateOpen(true);
  };

  const openEdit = (row) => {
    setSelectedSchedule(row);
    setCreateForm({
      target_type: String(row.target_type || "ASSET").toUpperCase().replace(/\s+/g, "_"),
      target_name: row.target_name || "",
      target_scope: row.target_scope || "",
      cadence: row.cadence || "MONTHLY",
      reminder_days: 7,
      vendor_contact_number: row.vendor_contact_number || "",
      notes: row.notes || "",
      next_due_at: row.next_due_at ? String(row.next_due_at).slice(0, 10) : "",
    });
    setCreateOpen(true);
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        target_type: createForm.target_type,
        target_name: createForm.target_name,
        target_scope: createForm.target_scope,
        cadence: createForm.cadence,
        reminder_days: Number(createForm.reminder_days) || 0,
        vendor_contact_number: createForm.vendor_contact_number,
        notes: createForm.notes,
        next_due_at: createForm.next_due_at || undefined,
      };
      if (selectedSchedule?.id) {
        await MaintenanceAPI.updateSchedule(selectedSchedule.id, payload);
      } else {
        await MaintenanceAPI.createSchedule(payload);
      }
      setCreateOpen(false);
      setSelectedSchedule(null);
      setCreateForm(DEFAULT_FORM);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to save maintenance schedule");
    } finally {
      setSaving(false);
    }
  };

  const onOpenClose = (row) => {
    setSelectedSchedule(row);
    setClosureComment("");
    setCloseOpen(true);
  };

  const onComplete = async () => {
    if (!selectedSchedule?.id) return;
    setSaving(true);
    setError("");
    try {
      await MaintenanceAPI.completeSchedule(selectedSchedule.id, { closure_comment: closureComment });
      setCloseOpen(false);
      setClosureComment("");
      setSelectedSchedule(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to complete maintenance schedule");
    } finally {
      setSaving(false);
    }
  };

  const onOpenUpload = (row) => {
    setSelectedSchedule(row);
    setReportFiles([]);
    setUploadOpen(true);
  };

  const onOpenInvoice = (row) => {
    setSelectedSchedule(row);
    setInvoiceFiles([]);
    setInvoiceForm({ invoice_number: "", invoice_amount: "", invoice_date: "" });
    setInvoiceOpen(true);
  };

  const onUploadInvoices = async () => {
    if (!selectedSchedule?.id || !invoiceFiles.length) {
      setError("Select at least one service invoice file.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await MaintenanceAPI.uploadInvoices(selectedSchedule.id, {
        files: invoiceFiles,
        invoiceNumber: invoiceForm.invoice_number,
        invoiceAmount: invoiceForm.invoice_amount,
        invoiceDate: invoiceForm.invoice_date,
      });
      setInvoiceOpen(false);
      setInvoiceFiles([]);
      setSelectedSchedule(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to upload service invoice");
    } finally {
      setSaving(false);
    }
  };

  const onOpenReopen = (row) => {
    setSelectedSchedule(row);
    setReopenReason("");
    setReopenOpen(true);
  };

  const onReopen = async () => {
    if (!selectedSchedule?.id) return;
    setSaving(true);
    setError("");
    try {
      await MaintenanceAPI.reopenSchedule(selectedSchedule.id, { reason: reopenReason });
      setReopenOpen(false);
      setReopenReason("");
      setSelectedSchedule(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to reopen work order");
    } finally {
      setSaving(false);
    }
  };

  const onUploadReports = async () => {
    if (!selectedSchedule?.id || !reportFiles.length) {
      setError("Select at least one maintenance report file.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await MaintenanceAPI.uploadReports(selectedSchedule.id, reportFiles);
      setUploadOpen(false);
      setReportFiles([]);
      setSelectedSchedule(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to upload maintenance report");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PremiumFeatureGate
      title="Maintenance Management"
      description="Centralize maintenance requests, scheduled work orders, evidence, and closure history in one operational queue."
    >
      <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: { xs: 1.5, md: 2.5 } }}>
        <Stack spacing={2}>
          <Paper sx={{ p: 2.5, border: "1px solid #dbe6f3", borderRadius: 2, background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)" }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  Maintenance Work Orders
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 860 }}>
                  Keep every repair, preventive service, vendor invoice, and closure decision against the asset record.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: "stretch", md: "center" } }}>
                <Tooltip title="Refresh maintenance data" arrow>
                  <IconButton
                    onClick={() => void load()}
                    disabled={loading}
                    aria-label="Refresh maintenance data"
                    sx={{
                      width: 44,
                      height: 44,
                      border: "1px solid",
                      borderColor: "primary.main",
                      color: "primary.main",
                      borderRadius: 2,
                      bgcolor: "white",
                      "&:hover": { bgcolor: "#eff6ff", borderColor: "primary.dark" },
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Create new maintenance schedule" arrow>
                  <IconButton
                    onClick={openCreate}
                    aria-label="Create new maintenance schedule"
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: "primary.main",
                      color: "white",
                      "&:hover": { bgcolor: "primary.dark" },
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
            {error ? <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert> : null}
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: "100%", borderRadius: 2, borderColor: "#dbe6f3" }}>
                <Typography sx={{ fontWeight: 800 }}>1. Report an issue</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  A custodian uses the asset QR page to request help. It arrives here as <strong>Requested</strong>; select <strong>Plan work</strong> to set the due date and vendor.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: "100%", borderRadius: 2, borderColor: "#dbe6f3" }}>
                <Typography sx={{ fontWeight: 800 }}>2. Plan preventive service</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Use the <strong>+</strong> button for planned maintenance. Add service evidence and invoices, then close the work order with a clear outcome comment.
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Open Requests" value={stats.requested ?? rows.filter((row) => row.status_key === "requested").length} hint={INFO_TEXT.requested} color="#0369a1" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Scheduled Work" value={stats.scheduled ?? rows.filter((row) => row.status_key === "active").length} hint={INFO_TEXT.scheduled} color="#166534" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Closed Work" value={stats.completed ?? rows.filter((row) => row.status_key === "completed").length} hint={INFO_TEXT.completed} color="#1d4ed8" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Service Invoices" value={stats.invoices ?? invoiceCount} hint={INFO_TEXT.invoices} color="#7c3aed" />
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #dbe6f3" }}>
            <SectionTitle
              icon={<NotificationsActiveIcon color="primary" />}
              title="Maintenance Queue"
              subtitle="A work order moves from Requested → Scheduled → Closed. Reopen only when work was not actually finished."
              action={
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label="Open"
                    color={statusFilter === "open" ? "primary" : "default"}
                    onClick={() => setStatusFilter("open")}
                    clickable
                  />
                  <Chip
                    label="Requested"
                    color={statusFilter === "requested" ? "primary" : "default"}
                    onClick={() => setStatusFilter("requested")}
                    clickable
                  />
                  <Chip
                    label="Scheduled"
                    color={statusFilter === "active" ? "primary" : "default"}
                    onClick={() => setStatusFilter("active")}
                    clickable
                  />
                  <Chip
                    label="Closed"
                    color={statusFilter === "completed" ? "primary" : "default"}
                    onClick={() => setStatusFilter("completed")}
                    clickable
                  />
                </Stack>
              }
            />
            <Alert severity="info" sx={{ mb: 2 }}>
              Each row is one work order. “Evidence” is the technician report or photos; “Invoice” is the vendor bill. Both stay attached to the record for audit.
            </Alert>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} sx={{ mb: 2 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search work item, scope, vendor, or note"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                label="Category"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 180 } }}
              >
                <MenuItem value="all">All categories</MenuItem>
                {typeOptions
                  .filter((type) => type !== "all")
                  .map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
              </TextField>
              <TextField size="small" type="date" label="Due from" value={queueDateFrom} onChange={(event) => setQueueDateFrom(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: { xs: "100%", md: 150 } }} />
              <TextField size="small" type="date" label="Due to" value={queueDateTo} onChange={(event) => setQueueDateTo(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: { xs: "100%", md: 150 } }} />
              <Button
                variant="text"
                startIcon={<FilterAltOutlinedIcon />}
                onClick={() => {
                  setStatusFilter("open");
                  setTypeFilter("all");
                  setSearchQuery("");
                  setQueueDateFrom("");
                  setQueueDateTo("");
                }}
                sx={{ whiteSpace: "nowrap", alignSelf: { xs: "flex-start", md: "center" } }}
              >
                Reset filters
              </Button>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {sortedVisibleRows.length} of {rows.length} records · click a column heading to sort
              </Typography>
            </Stack>
            {loading ? (
              <Alert severity="info">Loading maintenance records...</Alert>
            ) : (
              <MaintenanceRowTable
                rows={sortedVisibleRows}
                onEdit={openEdit}
                onUpload={onOpenUpload}
                onInvoice={onOpenInvoice}
                onComplete={onOpenClose}
                onReopen={onOpenReopen}
                sort={queueSort}
                onSort={(field) => toggleSort(setQueueSort, field)}
              />
            )}
          </Paper>

          <Paper sx={{ p: 2.5, borderRadius: 2, border: "1px solid #dbe6f3" }}>
            <SectionTitle
              icon={<AssignmentIcon color="primary" />}
              title="Maintenance Audit Trail"
              subtitle="Request submissions, scheduling updates, evidence uploads, and closure events."
            />
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Chip label={`All (${historyRows.length})`} color={historyFilter === "all" ? "primary" : "default"} onClick={() => setHistoryFilter("all")} clickable />
              <Chip label={`Assets (${historyRows.filter((row) => historyCategory(row) === "asset").length})`} color={historyFilter === "asset" ? "primary" : "default"} onClick={() => setHistoryFilter("asset")} clickable />
              <Chip label={`Other (${historyRows.filter((row) => historyCategory(row) === "other").length})`} color={historyFilter === "other" ? "primary" : "default"} onClick={() => setHistoryFilter("other")} clickable />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mb: 2 }}>
              <TextField size="small" type="date" label="Activity from" value={historyDateFrom} onChange={(event) => setHistoryDateFrom(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
              <TextField size="small" type="date" label="Activity to" value={historyDateTo} onChange={(event) => setHistoryDateTo(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
              <Button variant="text" onClick={() => { setHistoryDateFrom(""); setHistoryDateTo(""); }} sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}>Clear dates</Button>
            </Stack>
            {sortedHistoryRows.length ? (
              <TableContainer>
                <Table size="small" sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      {[["work_item", "Work Item"], ["category", "Category"], ["event_type", "Event"], ["message", "Details"], ["performed_at", "When"], ["actor_name", "By"]].map(([field, label]) => (
                        <TableCell key={field} sortDirection={historySort.by === field ? historySort.dir : false}>
                          <TableSortLabel active={historySort.by === field} direction={historySort.by === field ? historySort.dir : "asc"} onClick={() => toggleSort(setHistorySort, field)}>{label}</TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedHistoryRows.slice(0, 30).map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => setSelectedHistoryRow(row)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Stack spacing={0.25}>
                            <Typography sx={{ fontWeight: 700 }}>{historyWorkItem(row)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {historyScope(row)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={historyCategoryLabel(row)} color={historyCategory(row) === "asset" ? "success" : "default"} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={String(row.event_type || "").replace(/_/g, " ").toUpperCase()} />
                        </TableCell>
                        <TableCell>{row.message || "-"}</TableCell>
                        <TableCell>{formatDateTime(row.performed_at)}</TableCell>
                        <TableCell>{row.actor?.email || "system"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No audit records match the current filter.</Alert>
            )}
          </Paper>
        </Stack>
      </Box>

      <Drawer
        anchor="right"
        open={!!selectedHistoryRow}
        onClose={() => setSelectedHistoryRow(null)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 520 } } }}
      >
        {selectedHistoryRow ? (
          <Box sx={{ p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {historyWorkItem(selectedHistoryRow)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Audit event details
                </Typography>
              </Box>
              <Chip size="small" label={historyCategoryLabel(selectedHistoryRow)} color={historyCategory(selectedHistoryRow) === "asset" ? "success" : "default"} />
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Event
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {String(selectedHistoryRow.event_type || "").replace(/_/g, " ").toUpperCase()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  When
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {formatDateTime(selectedHistoryRow.performed_at)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  By
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {selectedHistoryRow.actor?.email || "system"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Message
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {selectedHistoryRow.message || "-"}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Schedule Context
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                  <Typography variant="body2">
                    <strong>State:</strong> {selectedHistoryRow.schedule?.status || selectedHistoryRow.status_snapshot || "-"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Due:</strong> {formatDateTime(selectedHistoryRow.due_at_snapshot || selectedHistoryRow.schedule?.next_due_at)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Vendor Contact:</strong> {selectedHistoryRow.schedule?.vendor_contact_number || "-"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Target Scope:</strong> {selectedHistoryRow.schedule?.target_scope || "-"}
                  </Typography>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Event details
                </Typography>
                {friendlyDetails(selectedHistoryRow.details).length ? (
                  <Paper variant="outlined" sx={{ mt: 0.75, borderRadius: 2, overflow: "hidden" }}>
                    {friendlyDetails(selectedHistoryRow.details).map((item, index) => (
                      <Stack key={`${item.label}-${index}`} direction={{ xs: "column", sm: "row" }} spacing={0.5} sx={{ px: 1.5, py: 1, borderBottom: index + 1 < friendlyDetails(selectedHistoryRow.details).length ? "1px solid #e5e7eb" : "none" }}>
                        <Typography variant="caption" color="text.secondary" sx={{ width: { sm: 190 }, flexShrink: 0 }}>{item.label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>{item.value}</Typography>
                      </Stack>
                    ))}
                  </Paper>
                ) : <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>No additional details were recorded for this event.</Typography>}
              </Box>

              {Array.isArray(selectedHistoryRow.report_items) && selectedHistoryRow.report_items.length ? (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Attached Reports
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.75 }}>
                      {selectedHistoryRow.report_items.map((item, index) => (
                        <Paper key={`${selectedHistoryRow.id}-report-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {item.original_name || item.url || `Report ${index + 1}`}
                          </Typography>
                          {item.url ? <Button component="a" href={item.url} target="_blank" rel="noopener noreferrer" size="small" sx={{ alignSelf: "flex-start", mt: 0.5 }}>Open attachment</Button> : <Typography variant="caption" color="text.secondary">Attachment link unavailable</Typography>}
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                </>
              ) : null}
            </Stack>
          </Box>
        ) : null}
      </Drawer>

      <Dialog open={closeOpen} onClose={() => !saving && setCloseOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Close work order</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="warning">Closing confirms that the work is complete. The closure comment becomes part of the permanent audit trail.</Alert>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 800 }}>{selectedSchedule?.target_name || "Work order"}</Typography>
              <Typography variant="body2" color="text.secondary">Add the actual fix, service result, or reason the item is resolved.</Typography>
            </Paper>
            <TextField
              autoFocus
              required
              multiline
              minRows={4}
              label="Closure comment"
              value={closureComment}
              onChange={(event) => setClosureComment(event.target.value)}
              placeholder="Example: Battery was replaced and the device was tested successfully."
              helperText="At least 5 characters are required. You can reopen the work order later with a reason."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="success" onClick={onComplete} disabled={saving || closureComment.trim().length < 5}>
            Confirm and close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reopenOpen} onClose={() => !saving && setReopenOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reopen work order</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="info">Use this only if the recorded work was incomplete, failed validation, or needs further action. The original closure remains in the audit trail.</Alert>
            <TextField
              autoFocus
              required
              multiline
              minRows={3}
              label="Reason for reopening"
              value={reopenReason}
              onChange={(event) => setReopenReason(event.target.value)}
              placeholder="Example: Replacement part failed after testing and vendor follow-up is required."
              helperText="At least 5 characters are required."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={onReopen} disabled={saving || reopenReason.trim().length < 5}>Reopen work order</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={invoiceOpen} onClose={() => !saving && setInvoiceOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Attach service invoice</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="info">Attach the vendor bill for this repair or service. This is separate from an asset purchase invoice.</Alert>
            <TextField label="Invoice number" value={invoiceForm.invoice_number} onChange={(event) => setInvoiceForm((current) => ({ ...current, invoice_number: event.target.value }))} />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField fullWidth type="number" label="Invoice amount" value={invoiceForm.invoice_amount} onChange={(event) => setInvoiceForm((current) => ({ ...current, invoice_amount: event.target.value }))} inputProps={{ min: 0, step: "0.01" }} />
              <TextField fullWidth type="date" label="Invoice date" value={invoiceForm.invoice_date} onChange={(event) => setInvoiceForm((current) => ({ ...current, invoice_date: event.target.value }))} InputLabelProps={{ shrink: true }} />
            </Stack>
            <Button component="label" variant="outlined" startIcon={<ReceiptLongIcon />}>
              {invoiceFiles.length ? `${invoiceFiles.length} invoice file(s) selected` : "Choose invoice file(s)"}
              <input hidden type="file" multiple accept=".pdf,.png,.jpg,.jpeg" onChange={(event) => setInvoiceFiles(Array.from(event.target.files || []))} />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={onUploadInvoices} disabled={saving || !invoiceFiles.length}>Attach invoice</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{selectedSchedule ? (selectedSchedule.status_key === "requested" ? "Plan requested work" : "Edit maintenance plan") : "Plan preventive maintenance"}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              select
              label="Target Type"
              value={createForm.target_type}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, target_type: e.target.value }))}
              SelectProps={{ native: false }}
            >
              <MenuItem value="ASSET">Asset</MenuItem>
              <MenuItem value="DEVICE">Device</MenuItem>
              <MenuItem value="SOFTWARE">Software</MenuItem>
              <MenuItem value="CUSTOM">Custom</MenuItem>
            </TextField>
            <TextField
              label="Target Name"
              value={createForm.target_name}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, target_name: e.target.value }))}
              placeholder="Asset UID, device name, or maintenance work title"
            />
            <TextField
              label="Target Scope"
              value={createForm.target_scope}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, target_scope: e.target.value }))}
              placeholder="Optional department, site, or asset grouping"
            />
            <TextField
              select
              label="Cadence"
              value={createForm.cadence}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, cadence: e.target.value }))}
            >
              <MenuItem value="WEEKLY">Weekly</MenuItem>
              <MenuItem value="MONTHLY">Monthly</MenuItem>
              <MenuItem value="QUARTERLY">Quarterly</MenuItem>
              <MenuItem value="YEARLY">Yearly</MenuItem>
              <MenuItem value="CUSTOM">Custom</MenuItem>
            </TextField>
            <TextField
              type="number"
              label="Reminder Days"
              value={createForm.reminder_days}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, reminder_days: e.target.value }))}
              helperText="How many days before due date the reminder should appear."
            />
            <TextField
              label="Vendor Contact Number"
              value={createForm.vendor_contact_number}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, vendor_contact_number: e.target.value }))}
              placeholder="Vendor or technician contact"
            />
            <TextField
              type="date"
              label="Next Due Date"
              value={createForm.next_due_at}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, next_due_at: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Notes"
              multiline
              minRows={3}
              value={createForm.notes}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="Service checklist, request details, or maintenance purpose."
            />
            <Alert severity="info">For a QR request, adding a due date moves it into Scheduled work. For preventive work, choose the next planned service date.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onSave} disabled={saving || !createForm.target_name.trim()}>
            {selectedSchedule?.status_key === "requested" ? "Schedule work" : selectedSchedule ? "Save changes" : "Create work order"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Upload Maintenance Report</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="info">
              Upload the service report, checklist, or technician evidence for this maintenance record.
            </Alert>
            {selectedSchedule ? (
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {selectedSchedule.target_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedSchedule.target_type} - {selectedSchedule.cadence} - Due {formatDate(selectedSchedule.next_due_at)}
                </Typography>
              </Paper>
            ) : null}
            <Button component="label" variant="outlined" startIcon={<DescriptionIcon />}>
              {reportFiles.length ? `${reportFiles.length} file(s) selected` : "Choose report file(s)"}
              <input
                hidden
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                onChange={(e) => setReportFiles(Array.from(e.target.files || []))}
              />
            </Button>
            <Alert severity={reportFiles.length ? "success" : "info"}>
              {reportFiles.length ? "Files selected and ready for upload." : "Attach one or more files before saving."}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onUploadReports} disabled={saving || !reportFiles.length}>
            Upload Report
          </Button>
        </DialogActions>
      </Dialog>
    </PremiumFeatureGate>
  );
}
